import { Flex, Skeleton, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import React, { type JSX } from "react";

import { useAnalytics } from "@/analytics";
import { useChallenges } from "@/sales-tools/challenges/api/useChallenges";
import { ChallengeProgression } from "@/sales-tools/challenges/component/ChallengeProgression";
import { findFirstActiveChallenge } from "@/sales-tools/challenges/utils/findFirstActiveChallenge";
import { useLogger } from "@/sdk";
import { userCache } from "@/shared-utils";

import classes from "./Challenge.module.css";
import { ChallengeDrawer } from "./component/ChallengeDrawer";
import { Clock } from "./icons/Clock";
import { sortTeams } from "./utils/findMyTeam";
import { getDaysLeftText } from "./utils/getDaysLeftText";

export function Challenge(): JSX.Element | null {
    const logger = useLogger({ module: "Challenge", supportTeam: "sales" });
    const { data, isLoading, isError } = useChallenges();
    const [opened, { open, close }] = useDisclosure();
    const { dispatcher } = useAnalytics();
    const empId = userCache.employeeId!;

    if (isLoading) {
        return (
            <Flex data-testid="skeleton-loader" className={classes.container} direction="column" fz="xs">
                <Flex align="center" direction="column" justify="center" h="72px">
                    <Skeleton h="16px" mt="xs" />
                    <Skeleton h="50px" mt="xs" />
                    <Skeleton h="14px" mt="xs" />
                </Flex>
            </Flex>
        );
    }

    const activeChallenge = findFirstActiveChallenge(logger, data);

    if (isError || !activeChallenge) return null;
    if (activeChallenge.challengeType === "team") {
        sortTeams(logger, activeChallenge?.details, empId);
    }

    const { details } = activeChallenge;
    return (
        <>
            <Flex
                data-testid="challenge-widget"
                className={classes.container}
                direction="column"
                onClick={() => {
                    open();
                    void dispatcher.dispatchBusinessEvent("TrophyCaseOpened");
                }}
            >
                <Flex className={classes.header} align="center" mb="xs">
                    <Text data-testid="challenge-header" fz="sm" c="dark.0" fw={700}>
                        {details.length === 1 ? "Solo" : "Team"} challenge
                    </Text>
                    <Flex mt="4xs" align="center">
                        <Clock />
                        <Text data-testid="challenge-days-left" ml="4xs" size="xxs" c="dark.3">
                            {getDaysLeftText(activeChallenge)}
                        </Text>
                    </Flex>
                </Flex>
                <ChallengeProgression displayDirection="vertical" challenge={activeChallenge} />
            </Flex>
            {opened && <ChallengeDrawer onClose={close} opened={opened} />}
        </>
    );
}
